import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Type that is cut to the block it is printed in — what the press does by hand
 * and what both memo sheets need.
 *
 * Each piece carries its own measurements as `data-fit-*`, because the export
 * measures the copy it is about to draw rather than trusting what the page
 * worked out (see `refit` in exportInvoice.js).
 */

/** How far a block's lines may be squeezed before they are simply clipped. */
export const MIN_FIT = 0.42;

/**
 * Half of the banner, set to fit.
 *
 * The banner is a fixed block, so its two halves are measured apart: the
 * contact lines fit the top, the shop's name and the lines under it fit the
 * bottom. Sized together, a shop with a proprietor, two numbers and an email
 * would squeeze its own name down with them; apart, the name keeps its size
 * however much is written above it.
 */
export function FitZone({ height, align, pad, signature, children }) {
    const ref = useRef(null);
    const [fit, setFit] = useState(1);

    const measure = useCallback(() => {
        const node = ref.current;
        if (!node) return;
        const room = height - pad.top - pad.bottom;
        const needed = node.offsetHeight;
        if (room <= 0 || needed <= 0) return;
        const next = needed <= room ? 1 : Math.max(MIN_FIT, room / needed);
        setFit((prev) => (Math.abs(prev - next) < 0.005 ? prev : next));
    }, [height, pad.top, pad.bottom]);

    useLayoutEffect(() => { measure(); }, [measure, signature]);

    // The Bangla face lands after the first paint and the lines grow with it,
    // so what was measured then is not what is printed. Measured again when the
    // font arrives and whenever the content's own height changes.
    useEffect(() => {
        let alive = true;
        const again = () => { if (alive) measure(); };
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(again);
        const node = ref.current;
        if (!node || typeof ResizeObserver === 'undefined') return () => { alive = false; };
        const ro = new ResizeObserver(again);
        ro.observe(node);
        return () => { alive = false; ro.disconnect(); };
    }, [measure]);

    return (
        <div
            data-fit-zone=""
            data-fit-room={height - pad.top - pad.bottom}
            data-fit-min={MIN_FIT}
            style={{
                position: 'relative', height, boxSizing: 'border-box', overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
                justifyContent: align === 'bottom' ? 'flex-end' : align === 'centre' ? 'center' : 'flex-start',
                padding: `${pad.top}px ${pad.side}px ${pad.bottom}px`,
            }}
        >
            {/* Scaled about the edge it is anchored to, so a squeezed half
                still sits against the same line of the block. */}
            <div
                ref={ref}
                style={{
                    width: '100%',
                    transform: fit === 1 ? undefined : `scale(${fit})`,
                    transformOrigin: align === 'bottom' ? 'bottom center' : align === 'centre' ? 'center' : 'top center',
                }}
            >
                {children}
            </div>
        </div>
    );
}

/**
 * One line, squeezed to the width it has. The press cuts the shop's name to
 * the block rather than running it over two lines, so the name is measured at
 * its full size and then set at whatever share of it fits.
 */
export function FitLine({ text, size, style, parts = null }) {
    const ref = useRef(null);
    const [fitted, setFitted] = useState(size);

    const measure = useCallback(() => {
        const node = ref.current;
        if (!node) return;
        const room = node.parentElement ? node.parentElement.clientWidth : 0;
        node.style.fontSize = `${size}px`;
        const natural = node.scrollWidth;
        const next = (!room || !natural || natural <= room) ? size : (size * room) / natural;
        node.style.fontSize = `${next}px`;
        setFitted((prev) => (Math.abs(prev - next) < 0.05 ? prev : next));
    }, [size]);

    useLayoutEffect(() => { measure(); }, [measure, text]);

    // The room a line has is settled by its neighbours, which only reach their
    // own width once the Bangla face has loaded — a line measured before that
    // runs past the block and is cut off by its edge.
    useEffect(() => {
        let alive = true;
        const again = () => { if (alive) measure(); };
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(again);
        const parent = ref.current ? ref.current.parentElement : null;
        if (!parent || typeof ResizeObserver === 'undefined') return () => { alive = false; };
        const ro = new ResizeObserver(again);
        ro.observe(parent);
        return () => { alive = false; ro.disconnect(); };
    }, [measure, text]);

    return (
        <div
            ref={ref}
            data-fit-line=""
            data-fit-size={size}
            style={{ ...style, fontSize: fitted, whiteSpace: 'nowrap' }}
        >
            {/* `parts` sets one line in more than one ink — the two-tone pad
                prints the first word of the shop's name in black. */}
            {parts
                ? parts.map((part, i) => <span key={i} style={{ color: part.color }}>{part.text}</span>)
                : text}
        </div>
    );
}
